/**
 * Global Ultra-Fast Image Optimizer & CDN Accelerator for Hamim Enterprises
 * 
 * Solves slow image loading on public domains & global browsers:
 * 1. Converts slow/unreliable sources to ultra-fast CDN edge endpoints.
 * 2. Dynamically optimizes Unsplash URLs to WebP with 400px width & 70% quality (instant <20KB files).
 * 3. Provides an instant SVG fallback if offline or blocked.
 * 4. Background non-blocking preloading via requestIdleCallback.
 */

// Instant vector SVG fallback for medical instruments (0ms, 0 bytes network)
export const SURGICAL_FALLBACK_SVG = "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200' width='200' height='200'%3E%3Crect width='200' height='200' rx='24' fill='%23f8fafc'/%3E%3Cpath d='M70 130 L130 70 M120 60 L140 80 M130 70 L145 55 A8 8 0 0 1 155 65 L140 80 M60 140 L70 130' stroke='%234f46e5' stroke-width='6' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3Ccircle cx='62' cy='145' r='14' stroke='%234f46e5' stroke-width='5' fill='none'/%3E%3Ctext x='100' y='178' font-family='sans-serif' font-size='10' font-weight='bold' fill='%2364748b' text-anchor='middle' letter-spacing='1'%3EHAMIM SURGICAL%3C/text%3E%3C/svg%3E";

// Curated high-speed medical instrument photos with global edge CDN caching
const FALLBACK_SURGICAL_CDN = [
  'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1584362917165-526a968579e8?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1551076805-e1869033e561?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1583912267670-6575ad362e3b?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1576091160550-217359f4ecf8?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?auto=format&fit=crop&w=400&q=70',
  'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=400&q=70'
];

/**
 * Optimizes an image URL for rapid delivery across global networks and all browsers.
 */
export function optimizeImageUrl(url?: string | null, width = 400, quality = 70): string {
  if (!url || typeof url !== 'string') {
    return FALLBACK_SURGICAL_CDN[0];
  }

  const trimmed = url.trim();

  // 1. Data URLs (Base64 / SVG) - already inlined in memory
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }

  // 2. Fix legacy / slow loremflickr.com URLs
  if (trimmed.includes('loremflickr.com')) {
    let hash = 0;
    for (let i = 0; i < trimmed.length; i++) {
      hash = (hash << 5) - hash + trimmed.charCodeAt(i);
      hash |= 0;
    }
    const idx = Math.abs(hash) % FALLBACK_SURGICAL_CDN.length;
    return FALLBACK_SURGICAL_CDN[idx];
  }

  // 3. Google Drive URLs -> Transform to Google's high-speed CDN thumbnail servers (lh3.googleusercontent.com)
  if (trimmed.includes('drive.google.com')) {
    const idMatch = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/) || trimmed.match(/id=([a-zA-Z0-9_-]+)/);
    if (idMatch && idMatch[1]) {
      return `https://lh3.googleusercontent.com/d/${idMatch[1]}=w${width}`;
    }
  }

  // 4. Unsplash Images -> enforce WebP modern compression & edge resizing
  if (trimmed.includes('images.unsplash.com')) {
    try {
      const urlObj = new URL(trimmed);
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('q', quality.toString());
      return urlObj.toString();
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}

// In-memory set of already preloaded image URLs
const preloadedUrls = new Set<string>();

/**
 * Preloads an image into the browser's HTTP cache asynchronously.
 */
export function preloadImage(src: string): Promise<void> {
  const optimized = optimizeImageUrl(src);
  if (!optimized || preloadedUrls.has(optimized) || optimized.startsWith('data:')) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      preloadedUrls.add(optimized);
      resolve();
    };
    img.onerror = () => {
      resolve();
    };
    img.src = optimized;
  });
}

/**
 * Batch preloads multiple product images in idle time.
 */
export function preloadProductBatch(urls: (string | undefined)[], limit = 16) {
  if (typeof window === 'undefined') return;

  const targets = urls.filter((u): u is string => Boolean(u)).slice(0, limit);
  const runner = (typeof window !== 'undefined' && 'requestIdleCallback' in window)
    ? (window as any).requestIdleCallback
    : (cb: () => void) => setTimeout(cb, 100);
  
  runner(() => {
    targets.forEach((url) => {
      preloadImage(url);
    });
  });
}
